import { TabSection } from './TabSection'
import { Toggle } from '../Toggle'
import { PrefixInput } from '../PrefixInput'
import type { ChannelConfigUpdate } from '../../types'
import styles from './ReviewRoutingTab.module.css'

interface Props {
  channelId: string
  sessionToken: string
  isAdmin: boolean
  draft: {
    scopedUserGroupTagging?: boolean
    reviewerTaggingEnabled?: boolean
    dmReviewersEnabled?: boolean
    crosspostEnabled?: boolean
    prefix?: string
  }
  onChange: (patch: Partial<ChannelConfigUpdate>) => void
}

export function ReviewRoutingTab({ channelId, sessionToken, isAdmin, draft, onChange }: Props) {
  const scoped = draft.scopedUserGroupTagging ?? true
  const reviewerTagging = draft.reviewerTaggingEnabled ?? false
  const dmReviewers = draft.dmReviewersEnabled ?? false
  const crosspost = draft.crosspostEnabled ?? false
  const prefix = draft.prefix ?? ''

  return (
    <TabSection
      accent="blue"
      icon="alt_route"
      title="Review Routing"
      subtitle="Control how the bot tags reviewers and where PR notifications are routed"
    >
      <div className={styles.scopedSection}>
        <Toggle
          label="Auto-tag the right engineers"
          hint={<>The bot auto-creates Slack user groups per repo — engineers join and leave automatically based on their GitHub activity. The more you work on a repo, the more likely you land in that handle. Refreshed weekly, fully tunable. <a href="https://github.toasttab.com/toasttab/slack-user-groups" target="_blank" rel="noopener noreferrer">Learn more</a></>}
          checked={scoped}
          onChange={v => onChange({ scopedUserGroupTagging: v })}
          noBorder
        />

        {scoped && (
          <PrefixInput
            channelId={channelId}
            sessionToken={sessionToken}
            value={prefix}
            onChange={v => onChange({ prefix: v })}
          />
        )}
      </div>

      <div className={styles.reviewerSection}>
        <div className={styles.reviewerSectionHeader}>
          <span className={styles.reviewerSectionLabel}>Reviewer notifications</span>
          <span className={styles.reviewerSectionHint}>We recommend enabling at least one of these</span>
        </div>

        <Toggle
          label="Tag reviewers in channel"
          hint="Mention requested reviewers who are members of this channel on the new PR notification and when added to an open PR"
          checked={reviewerTagging}
          onChange={v => onChange({ reviewerTaggingEnabled: v })}
        />

        <Toggle
          label="DM reviewers"
          hint="When a review is explicitly requested from a user, the bot DMs them with the PR details — so you don't have to ping them manually. One DM per reviewer per PR, even across multiple channels. Each DM includes an opt-out button — reviewers can stop receiving DMs at any time and resume whenever they like."
          checked={dmReviewers}
          onChange={v => onChange({ dmReviewersEnabled: v })}
          noBorder
        />
      </div>

      {isAdmin && (
        <div className={styles.crosspostSection}>
          <Toggle
            label="Crosspost to home channels"
            hint="When this channel picks up a PR for a repo it doesn't own, also post it to that repo's own home channel — as registered in Backstage."
            checked={crosspost}
            onChange={v => onChange({ crosspostEnabled: v })}
            noBorder
          />
        </div>
      )}
    </TabSection>
  )
}
